import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';

/**
 * DisabledAccountScreen - Displayed when a client's account is disabled
 * 
 * Shows a message explaining the account is disabled and provides
 * an option to submit a new inquiry.
 * 
 * Requirements: 1.5
 */
export function DisabledAccountScreen() {
  const navigate = useNavigate();

  const handleSubmitInquiry = useCallback(() => {
    navigate('/intake');
  }, [navigate]);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
        {/* Icon */}
        <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
          <svg 
            className="w-8 h-8 text-amber-600" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
            />
          </svg>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">
          Account Disabled
        </h1>

        {/* Message */}
        <p className="text-gray-600 mb-8">
          Your account has been disabled. If you believe this is an error, 
          please contact our office or submit a new inquiry to get in touch 
          with our team.
        </p>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleSubmitInquiry}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Submit New Inquiry
          </button>
          
          <button
            onClick={handleSignOut}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Sign Out
          </button>
        </div>

        {/* Contact Info */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Need help? Contact us at{' '}
            <a 
              href="tel:+1-555-123-4567" 
              className="text-blue-600 hover:text-blue-700"
            >
              (555) 123-4567
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
